import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import SnapChatIcon from '@shared/atoms/Icon/svg/snapchat.svg?react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import Input from '@shared/atoms/Input';
import IconButton from '@shared/atoms/IconButton';
import T from '@shared/atoms/Typography';
import Menu from '@shared/molecules/Menu';

import theme from '@shared/themes/default';
import { SOCIAL_TYPE } from '@shared/const';

import SocialIcon from './SocialIcon';

const Container = styled.div`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.s};
`;

const FieldWrapper = styled.div`
  margin-bottom: ${theme.spacing.s};
`;

const DynamicInputList = ({
  emptyLabel,
  inputs,
  onChange,
  title,
  isRequired,
  types,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleAddInput = () => {
    onChange([...inputs, { ...types[0], socialMediaTypeId: '7', url: '' }]);
  };

  const handleChangeInput = (index, value) => {
    const newList = [...inputs];
    newList[index] = { ...inputs[index], url: value };
    onChange(newList);
  };

  const handleDeleteInput = (index) => {
    onChange(inputs.filter((_, idx) => index !== idx));
  };

  const handleClearInput = (index) => {
    const newList = [...inputs];
    newList[index] = { ...inputs[index], url: '' };
    onChange(newList);
  };

  const getMenu = (options) => {
    return options.reduce((prev, { id, name: label, placeholder, icon }) => {
      if (
        inputs.find((input) => input.socialMediaTypeId === id) ||
        icon === 'www'
      )
        return prev;
      return [
        ...prev,
        {
          ...(icon === SOCIAL_TYPE.SNAPCHAT
            ? { svgIcon: SnapChatIcon }
            : { icon: (props) => <SocialIcon name={icon} {...props} /> }),
          label,
          handler: () => {
            onChange([
              ...inputs,
              {
                socialMediaTypeId: id,
                type: label,
                label,
                placeholder,
                url: '',
              },
            ]);
          },
        },
      ];
    }, []);
  };

  const customMenuHandler = (event) => {
    setAnchorEl(anchorEl === null ? event.currentTarget : null);
  };

  const isMultipleTypes = types.length > 1;
  const currentMenu = getMenu(types);

  return (
    <Container>
      <Header>
        <T>
          <>
            {title}
            {!isRequired && (
              <T component="span" fontWeight="medium" italic>
                {` (${t('generic.optional')})`}
              </T>
            )}
          </>
        </T>
        {(types.length === 1 || currentMenu.length > 0) && (
          <IconButton
            ariaLabel={t('generic.add')}
            icon={AddIcon}
            onClick={isMultipleTypes ? customMenuHandler : handleAddInput}
            type="secondary"
          />
        )}
      </Header>
      {isMultipleTypes && currentMenu.length > 0 && (
        <Menu
          freeMenu
          initAnchorEl={anchorEl}
          menuItems={currentMenu}
          setInitAnchorEl={setAnchorEl}
        />
      )}
      {inputs.length === 0 ? (
        <T
          color={theme.palette.disabledDark}
          sx={{
            textAlign: 'center',
            margin: `${theme.spacing.m1} ${theme.spacing.s}`,
          }}
        >
          {emptyLabel}
        </T>
      ) : (
        inputs.map(({ id, label, placeholder, url }, index) => (
          <FieldWrapper key={id}>
            {label && <T sx={{ mb: theme.spacing.xs }}>{label}</T>}
            <Input
              iconRight={
                <>
                  {url && url.length > 0 && (
                    <IconButton
                      ariaLabel={t('generic.close')}
                      icon={CloseIcon}
                      onClick={() => handleClearInput(index)}
                      small
                      type="transparent"
                      theme="light"
                    />
                  )}
                  <IconButton
                    ariaLabel={t('generic.delete')}
                    icon={DeleteOutlineIcon}
                    onClick={() => handleDeleteInput(index)}
                    small
                    type="transparent"
                    theme="light"
                  />
                </>
              }
              key={id}
              value={url}
              placeholder={placeholder}
              onChange={(ev) => handleChangeInput(index, ev.target.value)}
            />
          </FieldWrapper>
        ))
      )}
    </Container>
  );
};

DynamicInputList.propTypes = {
  emptyLabel: PropTypes.string,
  inputs: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  types: PropTypes.array.isRequired,
};

export default DynamicInputList;
